import React from "react";

// Global error boundary. Without this, any uncaught render/effect exception
// (e.g. a bad API response feeding an assumption like `array.length`)
// unmounts the entire SPA and the only recovery is a full page refresh.
// This catches it, shows a recoverable screen, and logs the error for support.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary-fallback"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            padding: "24px",
            textAlign: "center",
            background: "#0b0b0f",
            color: "#f4f4f5",
            fontFamily: "inherit",
          }}
        >
          <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>
            Something went wrong on this screen.
          </div>
          <div style={{ fontSize: "0.85rem", color: "rgba(244,244,245,0.6)", maxWidth: "480px" }}>
            The rest of the app is unaffected. You can try again, or reload the page if this
            keeps happening.
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              data-testid="error-boundary-retry-button"
              onClick={this.handleReset}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid rgba(244,244,245,0.2)",
                background: "transparent",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <button
              type="button"
              data-testid="error-boundary-reload-button"
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                background: "#22c55e",
                color: "#0b0b0f",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
